import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminNav from '../../components/AdminNav';
import Footer from '../../components/Footer';
import { Card, CardContent } from '../../components/ui/card';
import { getProperties, getEnquiries, getBids } from '../../api/api';
import { Building2, MessageSquare, Gavel, TrendingUp } from 'lucide-react';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    properties: 0,
    enquiries: 0,
    bids: 0,
    pendingBids: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [properties, enquiries, bids] = await Promise.all([
        getProperties(),
        getEnquiries(),
        getBids()
      ]);
      
      const pendingBids = bids.filter(bid => bid.status === 'pending').length;
      
      setStats({
        properties: properties.length,
        enquiries: enquiries.length,
        bids: bids.length,
        pendingBids
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Properties',
      value: stats.properties,
      icon: <Building2 size={32} />,
      color: '#d4a574',
      link: '/admin/properties'
    },
    {
      title: 'Total Enquiries',
      value: stats.enquiries,
      icon: <MessageSquare size={32} />,
      color: '#0f4c75',
      link: '/admin/enquiries'
    },
    {
      title: 'Total Bids',
      value: stats.bids,
      icon: <Gavel size={32} />,
      color: '#16213e',
      link: '/admin/bids'
    },
    {
      title: 'Pending Bids',
      value: stats.pendingBids,
      icon: <TrendingUp size={32} />,
      color: '#f59e0b',
      link: '/admin/bids'
    }
  ];

  return (
    <div className="min-h-screen" style={{ background: '#f8f9fa' }}>
      <AdminNav />

      <section className="text-white py-16" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f4c75 100%)' }}>
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-4">Admin Dashboard</h1>
          <p className="text-xl" style={{ color: '#e8e8e8' }}>Manage your properties, enquiries, and bids</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-lg" style={{ color: '#636e72' }}>Loading dashboard...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                {statCards.map((card, index) => (
                  <Link key={index} to={card.link}>
                    <Card className="rounded-2xl border-2 hover:shadow-lg transition-all" style={{ borderColor: '#e8e8e8' }}>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div style={{ color: card.color }}>
                            {card.icon}
                          </div>
                          <div className="text-right">
                            <p className="text-4xl font-bold" style={{ color: card.color }}>{card.value}</p>
                          </div>
                        </div>
                        <p className="font-semibold" style={{ color: '#1a1a2e' }}>{card.title}</p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Link to="/admin/properties" className="block">
                  <Card className="rounded-2xl border-2 hover:shadow-lg transition-all h-full" style={{ borderColor: '#e8e8e8' }}>
                    <CardContent className="p-8 text-center">
                      <Building2 size={48} className="mx-auto mb-4" style={{ color: '#d4a574' }} />
                      <h3 className="text-2xl font-bold mb-2" style={{ color: '#1a1a2e' }}>Manage Properties</h3>
                      <p style={{ color: '#636e72' }}>Add, edit, or delete property listings</p>
                    </CardContent>
                  </Card>
                </Link>

                <Link to="/admin/enquiries" className="block">
                  <Card className="rounded-2xl border-2 hover:shadow-lg transition-all h-full" style={{ borderColor: '#e8e8e8' }}>
                    <CardContent className="p-8 text-center">
                      <MessageSquare size={48} className="mx-auto mb-4" style={{ color: '#0f4c75' }} />
                      <h3 className="text-2xl font-bold mb-2" style={{ color: '#1a1a2e' }}>View Enquiries</h3>
                      <p style={{ color: '#636e72' }}>Review customer enquiries and contacts</p>
                    </CardContent>
                  </Card>
                </Link>

                <Link to="/admin/bids" className="block">
                  <Card className="rounded-2xl border-2 hover:shadow-lg transition-all h-full" style={{ borderColor: '#e8e8e8' }}>
                    <CardContent className="p-8 text-center">
                      <Gavel size={48} className="mx-auto mb-4" style={{ color: '#16213e' }} />
                      <h3 className="text-2xl font-bold mb-2" style={{ color: '#1a1a2e' }}>Manage Bids</h3>
                      <p style={{ color: '#636e72' }}>Accept or reject property bids</p>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdminDashboard;